import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Quote, Plus, Send, X, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/tracker";

/**
 * TestimoniosSection — bloque público de testimonios.
 *
 * Props:
 *  - limit: cuántos mostrar (default 6)
 *  - title / eyebrow: cabeceras
 *  - background: "dark" | "darker" (default "dark")
 *  - source: string para tracking ("home", "libro", "descargar"...)
 */
export default function TestimoniosSection({
    limit = 6,
    title = "Voces de quienes lo leyeron",
    eyebrow = "Testimonios · 13",
    background = "dark",
    source = "section",
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    async function reload() {
        try {
            const { data } = await api.get(`/testimonios?limit=${limit}`);
            setItems(Array.isArray(data) ? data : []);
        } catch {
            // silent — keep empty
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit]);

    // Auto-open modal cuando el email +3d redirige con ?testimonio=1
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("testimonio") === "1") {
            setFormOpen(true);
            track("testimonio_form_opened", {
                source: `${source}_email_followup`,
                order: params.get("order") || null,
                utm_source: params.get("utm_source"),
                utm_campaign: params.get("utm_campaign"),
            });
            // limpiar query param para no re-abrir al cambiar de tab
            params.delete("testimonio");
            const clean = params.toString();
            navigate(`${location.pathname}${clean ? `?${clean}` : ""}#testimonios`, {
                replace: true,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const bg = background === "darker" ? "bg-[#0a0a0a]" : "bg-[#0d0d0d]";

    return (
        <section
            id="testimonios"
            className={`py-20 md:py-24 ${bg}`}
            data-testid="testimonios-section"
        >
            <div className="container-x">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                    <div className="max-w-2xl">
                        <p className="eyebrow mb-3">{eyebrow}</p>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-[var(--paper-2)]">
                            {title}{" "}
                            <span className="text-[var(--danger)]">crudo</span>.
                        </h2>
                        <p className="font-mono text-sm text-[var(--ink-muted)] mt-4 leading-relaxed">
                            Sin filtros editoriales. Personas reales que
                            descargaron el libro y nos escribieron qué les pasó.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            track("testimonio_form_opened", { source });
                            setFormOpen(true);
                        }}
                        className="btn-brutal btn-brutal-ghost"
                        data-testid="testimonios-open-form"
                    >
                        <Plus className="w-4 h-4" />
                        Deja el tuyo
                    </button>
                </div>

                {loading ? (
                    <p className="font-mono text-xs text-[var(--ink-muted)]">
                        Cargando testimonios…
                    </p>
                ) : items.length === 0 ? (
                    <p className="font-mono text-sm text-[var(--ink-muted)]">
                        Todavía no hay testimonios públicos. Sé el primero.
                    </p>
                ) : (
                    <div
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                        data-testid="testimonios-grid"
                    >
                        {items.map((t) => (
                            <TestimonioCard key={t.id} t={t} />
                        ))}
                    </div>
                )}
            </div>

            {formOpen && (
                <TestimonioForm
                    source={source}
                    onClose={() => setFormOpen(false)}
                    onSent={() => {
                        setFormOpen(false);
                        // Re-fetch para mostrar el nuevo cuando admin lo apruebe.
                        reload();
                    }}
                />
            )}
        </section>
    );
}

function TestimonioCard({ t }) {
    return (
        <article
            className="border border-[var(--line)] bg-[#111] p-6 relative hover:border-[var(--warning)] transition-colors duration-200"
            data-testid={`testimonio-${t.id}`}
        >
            <Quote className="absolute top-4 right-4 w-5 h-5 text-[var(--danger)] opacity-40" />
            {t.featured && (
                <span className="absolute -top-2 -left-2 bg-[var(--warning)] text-black font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1">
                    Destacado
                </span>
            )}
            <p className="font-display italic text-lg text-[var(--paper-2)] leading-[1.45] mb-5">
                &ldquo;{t.message}&rdquo;
            </p>
            <div className="border-t border-dashed border-[var(--ink-dim)] pt-3">
                <p className="font-mono text-sm text-[var(--paper)] font-bold">
                    {t.name}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-1 flex flex-wrap gap-x-2 gap-y-1">
                    {t.location && <span>{t.location}</span>}
                    {t.location && t.role && (
                        <span className="text-[var(--ink-dim)]">·</span>
                    )}
                    {t.role && (
                        <span className="text-[var(--warning)]">{t.role}</span>
                    )}
                </p>
            </div>
        </article>
    );
}

const ROLES = [
    { value: "", label: "Sin etiqueta" },
    { value: "en proceso", label: "En proceso de recuperación" },
    { value: "sobrio", label: "Sobrio/a" },
    { value: "sobria · 6 meses", label: "Sobrio/a · 6 meses" },
    { value: "sobrio · 1 año", label: "Sobrio/a · 1 año" },
    { value: "sobrio · 3 años", label: "Sobrio/a · 3+ años" },
    { value: "familiar", label: "Familiar o cercano/a" },
    { value: "profesional", label: "Profesional de la salud" },
];

function TestimonioForm({ onClose, onSent, source }) {
    const [form, setForm] = useState({
        name: "",
        location: "",
        role: "",
        message: "",
        consent_public: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (form.message.trim().length < 20) {
            toast.error("Cuéntanos un poco más (mínimo 20 caracteres).");
            return;
        }
        if (!form.consent_public) {
            toast.error("Necesitamos tu consentimiento para publicarlo.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/testimonios", {
                name: form.name.trim(),
                location: form.location.trim() || null,
                role: form.role.trim() || null,
                message: form.message.trim(),
                consent_public: form.consent_public,
            });
            track("testimonio_submitted", { source });
            setSent(true);
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No pudimos enviar tu testimonio. Inténtalo de nuevo.",
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="testimonio-form-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-lg border-2 border-[var(--paper-2)] bg-[#0d0d0d] max-h-[90vh] overflow-y-auto"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 p-2 text-[var(--ink-muted)] hover:text-[var(--paper-2)]"
                    aria-label="Cerrar"
                    data-testid="testimonio-form-close"
                >
                    <X className="w-5 h-5" />
                </button>

                {sent ? (
                    <div className="p-8 md:p-10 text-center">
                        <CheckCircle2 className="w-16 h-16 text-[var(--warning)] mx-auto mb-5" />
                        <h3 className="font-display text-3xl text-[var(--paper-2)] mb-3 leading-tight">
                            Gracias por escribir.
                        </h3>
                        <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed mb-6">
                            Lo leemos uno por uno antes de publicarlo. Si
                            entra al sitio, vas a verlo en la sección de
                            testimonios en los próximos días.
                        </p>
                        <button
                            type="button"
                            onClick={onSent}
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="testimonio-form-close-after-sent"
                        >
                            <Heart className="w-4 h-4" />
                            Cerrar
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="p-8 md:p-10 space-y-5"
                        data-testid="testimonio-form"
                    >
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2">
                                Deja tu testimonio
                            </p>
                            <h3 className="font-display text-2xl md:text-3xl text-[var(--paper-2)] leading-tight">
                                Cuéntanos qué te pasó al leerlo.
                            </h3>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2 leading-relaxed">
                                No necesitas hablar bonito. Habla honesto.
                                Antes de publicarse pasa por una moderación
                                rápida.
                            </p>
                        </div>

                        <Field
                            label="Tu nombre o un alias"
                            value={form.name}
                            onChange={(v) => setForm({ ...form, name: v })}
                            placeholder='Ej: "Camila R." o "Anónimo"'
                            testid="testimonio-name"
                            required
                            maxLength={80}
                        />
                        <Field
                            label="Ciudad (opcional)"
                            value={form.location}
                            onChange={(v) => setForm({ ...form, location: v })}
                            placeholder="Ej: Santiago, Chile"
                            testid="testimonio-location"
                            maxLength={80}
                        />
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] block mb-2">
                                Cómo te identificas (opcional)
                            </label>
                            <select
                                value={form.role}
                                onChange={(e) =>
                                    setForm({ ...form, role: e.target.value })
                                }
                                className="w-full bg-black border border-[var(--ink-dim)] px-3 py-2.5 font-mono text-sm text-[var(--paper-2)] focus:border-[var(--warning)] outline-none"
                                data-testid="testimonio-role"
                            >
                                {ROLES.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] block mb-2">
                                Tu testimonio
                                <span className="ml-2 normal-case tracking-normal text-[var(--ink-dim)]">
                                    ({form.message.length}/1000)
                                </span>
                            </label>
                            <textarea
                                value={form.message}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        message: e.target.value.slice(0, 1000),
                                    })
                                }
                                rows={5}
                                placeholder="Lo que sentiste leyéndolo, dónde te impactó, qué cambió (o no) después…"
                                className="w-full bg-black border border-[var(--ink-dim)] px-3 py-2.5 font-mono text-sm text-[var(--paper-2)] focus:border-[var(--warning)] outline-none leading-relaxed resize-y"
                                data-testid="testimonio-message"
                                required
                            />
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.consent_public}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        consent_public: e.target.checked,
                                    })
                                }
                                className="mt-1 cursor-pointer accent-[var(--danger)]"
                                data-testid="testimonio-consent"
                            />
                            <span className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed">
                                Acepto que mi nombre, ciudad y testimonio sean
                                publicados en el sitio. No compartimos tu email
                                ni datos privados.
                            </span>
                        </label>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={`btn-brutal btn-brutal-danger w-full justify-center ${
                                submitting ? "opacity-60 cursor-wait" : ""
                            }`}
                            data-testid="testimonio-submit"
                        >
                            <Send className="w-4 h-4" />
                            {submitting ? "Enviando…" : "Enviar mi testimonio"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({
    label,
    value,
    onChange,
    placeholder,
    testid,
    required,
    maxLength,
}) {
    return (
        <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] block mb-2">
                {label}
            </label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                maxLength={maxLength}
                data-testid={testid}
                className="w-full bg-black border border-[var(--ink-dim)] px-3 py-2.5 font-mono text-sm text-[var(--paper-2)] focus:border-[var(--warning)] outline-none"
            />
        </div>
    );
}
